import supabase from '../supabaseClient'
import { uploadPublicImageWithThumb } from './imageThumbUpload'
import { TABLE_NAMES } from '../config'
import { communitySportLabel } from './communitySport'
import { consumeGameCredit } from './consumeGameCredit'
import { getYoutubeLinkValidationReason, isStoredMatchFootageUrl, notifyAdmins } from '../components/academy-schedule/academySchedule.helpers'
import { nameFor } from '../components/signup/countries'
import { ensureInitialRatings } from './signupUserPersist'
import { generateJoinCode } from '../utils/joinCode'
import { persistCommunityContext } from '../utils/communityAccessCache'
import { writeLastPortal } from '../utils/lastPortal'

const AUTH_FLAG = 'statbox.quickUpload.auth'
const OPEN_KEY = 'statbox.quickUpload.open'
const URL_KEY = 'statbox.quickUpload.url'
const INTENT_KEY = 'statbox.quickUpload.intent'
const PAY_WINDOW_MS = 2 * 60 * 60 * 1000
const LOGO_BUCKET = 'community_logos'
const MAX_LOGO_BYTES = 5 * 1024 * 1024

async function saveCommunityLogo(communityId, file) {
  if (!file) return { ok: true, url: null, thumbUrl: null }
  if (!String(file.type || '').startsWith('image/')) {
    return { ok: false, error: 'The community logo needs to be an image.' }
  }
  if (file.size > MAX_LOGO_BYTES) {
    return { ok: false, error: 'That logo is too large. Use an image under 5 MB.' }
  }
  const ext = (String(file.name || '').split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png'
  const path = `${communityId}/${Date.now()}.${ext}`
  try {
    const { publicUrl, thumbPublicUrl } = await uploadPublicImageWithThumb(supabase, {
      bucket: LOGO_BUCKET,
      path,
      file,
    })
    return { ok: true, url: publicUrl, thumbUrl: thumbPublicUrl }
  } catch (uploadError) {
    return { ok: false, error: uploadError.message || 'Could not upload the community logo.' }
  }
}

async function saveCommunityRegionAndLogo(communityId, { region, logoFile, logoUrl }) {
  const patch = {}
  if (region) patch.region = region
  if (logoUrl) patch.logo_url = logoUrl
  if (logoFile) {
    const uploaded = await saveCommunityLogo(communityId, logoFile)
    if (!uploaded.ok) return uploaded
    if (uploaded.url) patch.logo_url = uploaded.url
    if (uploaded.thumbUrl) patch.logo_thumb_url = uploaded.thumbUrl
  }
  if (Object.keys(patch).length === 0) return { ok: true }
  const { error } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .update(patch)
    .eq('id', communityId)
  if (error) return { ok: false, error: error.message || 'Could not save the community logo.' }
  return { ok: true }
}

/** Stores the logo before a community row exists, so payment can finish first. */
export async function uploadQuickCommunityLogo(userId, file) {
  if (!userId) return { ok: false, error: 'Sign in again before uploading the logo.' }
  return saveCommunityLogo(userId, file)
}

export function markQuickUploadAuth() {
  try {
    sessionStorage.setItem(AUTH_FLAG, '1')
  } catch {
    /* storage unavailable */
  }
}

export function consumeQuickUploadAuth() {
  try {
    const pending = sessionStorage.getItem(AUTH_FLAG) === '1'
    if (pending) sessionStorage.removeItem(AUTH_FLAG)
    return pending
  } catch {
    return false
  }
}

export function markQuickUploadOpen() {
  try {
    sessionStorage.setItem(OPEN_KEY, '1')
  } catch {
    /* storage unavailable */
  }
}

export function quickUploadDialogRequested() {
  try {
    return sessionStorage.getItem(OPEN_KEY) === '1'
  } catch {
    return false
  }
}

export function clearQuickUploadOpen() {
  try {
    sessionStorage.removeItem(OPEN_KEY)
  } catch {
    /* storage unavailable */
  }
}

export function requestQuickUploadDialog() {
  markQuickUploadOpen()
  window.dispatchEvent(new CustomEvent('statbox-quick-upload-open'))
}

export function rememberQuickUploadUrl(url) {
  try {
    const next = String(url || '').trim()
    if (next) sessionStorage.setItem(URL_KEY, next)
    else sessionStorage.removeItem(URL_KEY)
  } catch {
    /* storage unavailable */
  }
}

export function readQuickUploadUrl() {
  try {
    return sessionStorage.getItem(URL_KEY) || ''
  } catch {
    return ''
  }
}

function escapeIlike(value) {
  return String(value || '').replace(/[%_\\]/g, '\\$&')
}

/** Case-insensitive exact match. Names are not unique in the database, so this is the gate. */
export async function communityNameTaken(teamName) {
  const team = String(teamName || '').trim()
  if (!team) return { ok: true, taken: false }
  const { data, error } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .select('id')
    .ilike('name', escapeIlike(team))
    .limit(1)
  if (error) {
    return { ok: false, error: error.message || 'Could not check that community name.' }
  }
  return { ok: true, taken: Boolean(data?.length) }
}

export function writeQuickUploadIntent(intent) {
  try {
    sessionStorage.setItem(
      INTENT_KEY,
      JSON.stringify({ ...intent, createdAt: intent?.createdAt || Date.now() }),
    )
  } catch {
    /* storage unavailable */
  }
}

export function patchQuickUploadIntent(partial) {
  const current = readQuickUploadIntent() || {}
  writeQuickUploadIntent({ ...current, ...partial })
}

export function readQuickUploadIntent() {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export function clearQuickUploadIntent() {
  try {
    sessionStorage.removeItem(INTENT_KEY)
    sessionStorage.removeItem(URL_KEY)
  } catch {
    /* storage unavailable */
  }
}

/** True only for a payment that was started recently, so a later login is not trapped on checkout. */
export function quickUploadWantsPayment(intent = readQuickUploadIntent()) {
  if (!intent || intent.stage !== 'pay') return false
  const createdAt = Number(intent.createdAt) || 0
  return createdAt > 0 && Date.now() - createdAt < PAY_WINDOW_MS
}

/**
 * Creates the signed-in user's community from a name and a team.
 * Skips the activation-call signup so this path never opens Calendly.
 */
export async function ensureQuickCommunity({ fullName, teamName, sport, countryIso2, logoFile, logoUrl }) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser()

  if (userError || !user) {
    return { ok: false, error: 'Sign in again to create your community.' }
  }

  const name = String(fullName || '').trim()
  const team = String(teamName || '').trim()
  const countryName = nameFor(countryIso2)
  if (!name) return { ok: false, error: 'Enter your name.' }
  if (!team) return { ok: false, error: 'Enter your community name.' }
  if (!countryName) return { ok: false, error: 'Choose a country.' }

  const { data: existing, error: fetchError } = await supabase
    .from(TABLE_NAMES.USERS)
    .select('user_id, community_id')
    .eq('user_id', user.id)
    .maybeSingle()

  if (fetchError) {
    return { ok: false, error: fetchError.message || 'Could not load your profile.' }
  }

  if (existing?.community_id) {
    const saved = await saveCommunityRegionAndLogo(existing.community_id, {
      region: countryName,
      logoFile,
      logoUrl,
    })
    if (!saved.ok) return { ok: false, error: saved.error }
    writeLastPortal('community')
    return { ok: true, communityId: existing.community_id, already: true }
  }

  const taken = await communityNameTaken(team)
  if (!taken.ok) return { ok: false, error: taken.error }
  if (taken.taken) {
    return { ok: false, error: 'That community name is already taken. Choose another.' }
  }

  const userPayload = {
    email: user.email || null,
    name,
    community_manager: true,
  }

  if (existing) {
    const { error: updateError } = await supabase
      .from(TABLE_NAMES.USERS)
      .update(userPayload)
      .eq('user_id', user.id)
    if (updateError) {
      return { ok: false, error: updateError.message || 'Could not save your name.' }
    }
  } else {
    const { error: insertError } = await supabase.from(TABLE_NAMES.USERS).insert([
      {
        user_id: user.id,
        ...userPayload,
        privacy_policy_terms: 'yes',
      },
    ])
    if (insertError) {
      return { ok: false, error: insertError.message || 'Could not create your account.' }
    }
    try {
      await ensureInitialRatings(user.id)
    } catch (ratingErr) {
      console.error('Initial ratings insert failed', ratingErr)
    }
  }

  const { data: community, error: communityError } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .insert({
      name: team,
      sport: communitySportLabel(sport),
      region: countryName,
      created_by: user.id,
      community_id_join: generateJoinCode(),
    })
    .select('id, name')
    .single()

  if (communityError || !community?.id) {
    return {
      ok: false,
      error: communityError?.message || 'Could not create your community.',
    }
  }

  const saved = await saveCommunityRegionAndLogo(community.id, { logoFile, logoUrl })
  if (!saved.ok) return { ok: false, error: saved.error }

  const { error: linkError } = await supabase
    .from(TABLE_NAMES.USERS)
    .update({ community_id: community.id, community_manager: true })
    .eq('user_id', user.id)

  if (linkError) {
    return {
      ok: false,
      error: 'Your community was created, but the profile did not link. Email support@statbox.co.',
    }
  }

  persistCommunityContext({
    communityName: community.name,
    managerName: name,
    userId: user.id,
  })
  writeLastPortal('community')
  return { ok: true, communityId: community.id, already: false }
}

function footageUrlFromIntent(intent) {
  const youtubeUrl = String(intent?.youtubeUrl || '').trim()
  const uploadedUrl = String(intent?.videoUrl || '').trim()
  if (intent?.source === 'youtube' && youtubeUrl && !getYoutubeLinkValidationReason(youtubeUrl)) return youtubeUrl
  if (intent?.source === 'video' && isStoredMatchFootageUrl(uploadedUrl)) return uploadedUrl
  return ''
}

/** Creates one game from the paid upload and spends the credit that was just bought. */
export async function createQuickUploadGame({ communityId, communityName, intent }) {
  const footageUrl = footageUrlFromIntent(intent)
  if (!communityId) return { ok: false, error: 'The community is not ready yet.' }
  if (!footageUrl) return { ok: false, error: 'The footage from this payment is missing. Upload the video again.' }

  const { data: existing, error: existingError } = await supabase
    .from(TABLE_NAMES.GAMES)
    .select('match_id')
    .eq('community_id', communityId)
    .eq('game_video_link', footageUrl)
    .limit(1)
    .maybeSingle()
  if (existingError) return { ok: false, error: existingError.message || 'Could not look up the game.' }

  let matchId = existing?.match_id || intent?.matchId || ''
  if (!matchId) {
    const now = new Date()
    const pad = (value) => String(value).padStart(2, '0')
    const datePlayed = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    const hour = `${pad(now.getHours())}:00:00`
    matchId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `match-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`
    const scheduleId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `schedule-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`
    const venue = String(communityName || intent?.teamName || 'Community').trim() || 'Community'
    const sport = communitySportLabel(intent?.sport)
    const createdBy = String(intent?.fullName || '').trim() || 'Community Manager'
    const keyCode = Math.random().toString(36).substring(2, 8).toUpperCase()
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const { error: gameError } = await supabase.from(TABLE_NAMES.GAMES).insert({
      match_id: matchId,
      sport,
      date_played: datePlayed,
      hour_slot: hour,
      venue,
      pitch: 'Main',
      promo_code: null,
      participants: [],
      created_by: null,
      venue_code: null,
      community_id: communityId,
      team_a_score: 0,
      team_b_score: 0,
      key_code: keyCode,
      game_video_link: footageUrl,
      game_video_link_vimeo: null,
      upload_link: null,
      upload_later: null,
      league_game: false,
      team_number: '2',
      extra_players_metadata: {
        schedule_id: scheduleId,
        use_custom_link: true,
        custom_link_type: 'link',
        custom_link_url: footageUrl,
        custom_venue_name: venue,
        custom_pitch_name: 'Main',
        visibility: 'community',
        publish_to_statbox: false,
        billing_status: 'waived',
        game_type: 'statbox',
      },
    })
    if (gameError) return { ok: false, error: gameError.message || 'Could not create the game.' }

    await supabase.from(TABLE_NAMES.SCHEDULES).insert({
      id: scheduleId,
      community_id: communityId,
      title: venue,
      match_id: matchId,
      day_of_week: days[now.getDay()],
      time: hour.slice(0, 5),
      venue,
      pitch: 'Main',
      start_date: datePlayed,
      repeat_weeks: 0,
      is_active: true,
    })

    notifyAdmins({
      venue,
      pitch: 'Main',
      date: datePlayed,
      time: hour.slice(0, 5),
      sport,
      source: 'Community',
      createdBy,
      community: venue,
      keyCode,
      numberOfGames: 1,
      dashboardUrl: 'https://www.statbox.co/admin/communities',
      message: `${createdBy} created a ${sport} game at ${venue}.`,
    }).catch((err) => console.error('Error sending admin notification:', err))
  }

  try {
    await consumeGameCredit(matchId)
  } catch (err) {
    const message = err?.message || 'The game was created, but the credit was not used.'
    if (!/already consumed/i.test(message)) return { ok: false, error: message, matchId }
  }
  return { ok: true, matchId }
}
